import { IWidget } from 'common/schema/widget/Widget';
import { WidgetInputType } from 'common/schema/widget/input/WidgetInputType';
import { useField, useFormikContext } from 'formik';
import {
  IRequestValues,
  IRequestListValue,
  IRequestListRow
} from 'common/schema/request/Request';
import { MetadataLogic } from 'common/schema/metadata/MetadataLogic';
import { useCallback } from 'react';
import { without } from 'lodash';

/**
 * Gestisce la compilazione di un metadato di tipo Lista
 */
export function useWidgetListCompile(
  widget: IWidget,
  excludePlaceholder?: boolean
) {
  if (
    !widget.input ||
    (widget.input.type !== WidgetInputType.List &&
      widget.input.type !== WidgetInputType.MultiPreference)
  ) {
    throw new Error(`Impossibile gestire con "useCompileNested" widget di tipo diverso dalle Liste`); // prettier-ignore
  }
  const formik = useFormikContext();
  const metadata = MetadataLogic.singleFromWidget(widget);
  const metadataName = `values.${metadata.id}`;
  const [field, meta, helpers] = useField<IRequestListValue>(metadataName);

  const rows = field.value ?? [];
  /**
   * Aggiungo una riga alla lista
   */
  const addRow = useCallback(() => {
    const nextIndex = rows.length;
    const nextRow: IRequestListRow = !excludePlaceholder
      ? { isPlaceholder: true, values: {} }
      : { values: {} };

    // ATTENZIONE:
    // non posso usare `helpers.setValue` perché in caso di modifica di una colonna seguita dall'aggiunta di una riga il valore della colonna viene persa
    // utilizzerò `helpers.setValue` per l'aggiornamento del campo (vedi multiPreference)
    // mentre `formik.setFieldValue` per l'aggiunta della riga

    // helpers.setValue(nextRows);
    formik.setFieldValue(`${metadataName}[${nextIndex}]`, nextRow);

    return nextRow;
  }, [rows, formik]);

  /**
   * Modifico una riga della lista
   */
  const updateRow = useCallback(
    (previous: IRequestListRow, row: IRequestListRow) => {
      const index = field.value.indexOf(previous);
      formik.setFieldValue(`${metadataName}[${index}]`, row);
    },
    [rows]
  );

  /**
   * Elimino una riga
   */
  const removeRow = useCallback(
    (row: IRequestListRow) => {
      helpers.setValue(without(rows, row));
      // Imposto un timeout per correggere i valori da validare
      setTimeout(() => {
        formik.setFieldTouched(`${metadataName}[${rows.length - 1}]`, false);
      }, 0);
    },
    [rows, helpers, formik]
  );

  return {
    rows,
    addRow,
    updateRow,
    removeRow
  };
}
